import React from "react";
import { useForm } from "react-hook-form";
import { Input, SelectWithRadio } from "@/components/form";
import { useAppSelector } from "@/hooks";
import { selectRoleOptions, selectUserLoading } from "@/store/slices/userSlice";
import { User } from "@/types/user.model";
import { capitalizeFirstLetter } from "@/utils/constants";

interface NewUserFormProps {
  onSubmit: (data: User) => void;
  onCancel: () => void;
  initialData?: User;
  isEditMode?: boolean;
}

export interface NewUserFormData {
  first_name: string;
  last_name: string;
  email: string;
  contact_number: string;
  role_id: string;
  status?: "active" | "inactive" | "pending";
}

const NewUserForm: React.FC<NewUserFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isEditMode = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
  } = useForm<NewUserFormData>({
    mode: "all",
    criteriaMode: "firstError",
    reValidateMode: "onChange",
    defaultValues: initialData || {
      first_name: "",
      last_name: "",
      email: "",
      contact_number: "",
      role_id: "",
      status: "active",
    },
  });

  // Track if the form has been modified
  const [isFormModified, setIsFormModified] = React.useState(false);

  // Watch all form values to detect changes
  const formValues = watch();

  // Trigger validation immediately when the form is mounted in edit mode
  React.useEffect(() => {
    if (initialData && isEditMode) {
      // Use setTimeout to ensure validation runs after the component has fully mounted
      // This helps ensure all field registrations are complete
      setTimeout(() => {
        trigger();
      }, 0);
    }
  }, [initialData, trigger, isEditMode]);

  // Check if form values have changed from initial data
  React.useEffect(() => {
    if (initialData && isEditMode) {
      // Compare current form values with initial data
      const hasChanged =
        formValues.first_name !== initialData.first_name ||
        formValues.last_name !== initialData.last_name ||
        formValues.email !== initialData.email ||
        formValues.contact_number !== initialData.contact_number ||
        formValues.role_id !== initialData.role_id ||
        formValues.status !== initialData.status;

      setIsFormModified(hasChanged);
    }
  }, [formValues, initialData, isEditMode]);

  // Get role options from Redux store without re-fetching
  const roleOptionsFromStore = useAppSelector(selectRoleOptions);

  // Map role options from the Redux store
  const roleOptions = roleOptionsFromStore.map((role) => ({
    value: role.id,
    label: capitalizeFirstLetter(role.name),
  }));

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    // { value: "pending", label: "Pending" },
  ];

  // Get loading state from Redux store
  const isLoading = useAppSelector(selectUserLoading);

  const submitForm = (data: User) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="mb-5 px-4 py-2 overflow-y-auto max-h-[calc(100vh-200px)]">
        <p className="text-[#6C6C6C] text-[14px] mb-4">
          <span className="text-[#DC2626]">*</span>Indicates mandatory fields
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input
            id="first_name"
            label="First name"
            type="text"
            placeholder="Enter first name"
            autoFocus={!isEditMode && !initialData}
            error={errors.first_name ? "First name is required" : undefined}
            required={true}
            maxLength={25}
            {...register("first_name", {
              required: {
                value: true,
                message: "First name is required",
              },
            })}
          />
          <Input
            id="last_name"
            label="Last name"
            type="text"
            placeholder="Enter last name"
            error={errors.last_name ? "Last name is required" : undefined}
            required={true}
            maxLength={25}
            {...register("last_name", {
              required: {
                value: true,
                message: "Last name is required",
              },
            })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input
            id="email"
            label="Email address"
            type="email"
            placeholder="Enter email address"
            readOnly={isEditMode}
            error={errors.email ? "Valid email is required" : undefined}
            required={true}
            maxLength={50}
            {...register("email", {
              required: {
                value: true,
                message: "Email is required",
              },
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            })}
          />
          <Input
            id="contact_number"
            label="Phone number"
            type="tel"
            placeholder="Enter phone number"
            error={errors.contact_number?.message as string}
            required={true}
            {...register("contact_number", {
              required: {
                value: true,
                message: "Phone number is required",
              },
              maxLength: {
                value: 15,
                message: "Phone number must be at most 15 characters",
              },
              // pattern: {
              //   value: /^\+?[0-9]+$/,
              //   message: "Invalid phone number format.",
              // },
            })}
          />
        </div>

        <div
          className={
            isEditMode
              ? "grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
              : "grid grid-cols-1 gap-4 mb-4"
          }
        >
          <div className="mb-4">
            <SelectWithRadio
              id="role_id"
              label="Role"
              placeholder="Select a role"
              options={roleOptions}
              value={watch("role_id") as string}
              onChange={(value) => {
                setValue(
                  "role_id",
                  value as "Contractor" | "Employee" | "Admin" | "Client",
                  {
                    shouldValidate: true,
                  }
                );
                trigger("role_id");
              }}
              error={errors.role_id ? "Role is required" : undefined}
              required={true}
            />
            <input
              type="hidden"
              {...register("role_id", {
                required: {
                  value: true,
                  message: "Role is required",
                },
              })}
            />
          </div>
          {isEditMode && (
            <div className="mb-4">
              <SelectWithRadio
                id="status"
                label="Status"
                placeholder="Select a status"
                options={statusOptions}
                value={watch("status") || "active"}
                onChange={(value) =>
                  setValue("status", value as "active" | "inactive", {
                    shouldValidate: true,
                  })
                }
                error={errors.status ? "Status is required" : undefined}
                required={isEditMode}
              />
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4  border-t border-gray-200 flex justify-end gap-2 rounded-b-[10px]">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-[14px] w-[147px] text-gray-700 bg-[#F5F5F5] rounded-md hover:bg-gray-50 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="w-[147px] h-[42px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[4px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed flex items-center justify-center"
          disabled={!isValid || isLoading || (isEditMode && !isFormModified)}
        >
          {isLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {isEditMode ? "Updating..." : "Adding..."}
            </>
          ) : (
            <>{isEditMode ? "Update" : "Add user"}</>
          )}
        </button>
      </div>
    </form>
  );
};

export default NewUserForm;
